import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { AcceptOrgInvitationRequest, GetOrgInvitationResponse } from '@nadoutong/contracts';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { acceptInvitation, getInvitation } from '../api/orgs.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatTimestamp } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, cx } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';

/**
 * Invitation landing (`/invite/:token`) — Dispatch Deck rebuild (epic
 * #897 PR 8, formerly the console-shared `createInvite` factory
 * instantiation).
 *
 * Wire behavior preserved: fetch the invitation summary (org, inviter,
 * invitee email, role, expiry — label/value rows, emails and expiry
 * mono); when the active session already matches the invitee email the
 * page offers a direct join button, otherwise a display-name + password
 * form creates the account on accept. Accept → session refresh →
 * `/dashboard` with a success toast (success confirmations may toast).
 *
 * Errors are single-channel (epic AC 7): validation and accept failures
 * render inline only — the legacy inline+toast double-report is gone.
 */

type InviteRole = 'admin' | 'member' | 'finance';

type FetchState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly summary: GetOrgInvitationResponse }
  | { readonly kind: 'error'; readonly message: string };

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

type TranslateFn = ReturnType<typeof useT>;

function invitationRoleLabel(t: TranslateFn, role: InviteRole): string {
  return role === 'admin'
    ? t('settings.invite_modal.role.admin')
    : t('settings.invite_modal.role.member');
}

export function Invite(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { fetchImpl, refresh, status, user } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();
  const [fetchState, setFetchState] = useState<FetchState>(
    token === undefined || token.length === 0
      ? { kind: 'error', message: t('invite.error.missing_token') }
      : { kind: 'loading' },
  );
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (token === undefined || token.length === 0) {
      return;
    }

    let cancelled = false;
    setFetchState({ kind: 'loading' });

    (async () => {
      try {
        const summary = await getInvitation(token, fetchImpl);
        if (!cancelled) {
          setFetchState({ kind: 'loaded', summary });
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setFetchState({ kind: 'error', message: coerceErrorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchImpl]);

  const activeSessionMatchesInvitee =
    status === 'authenticated' &&
    fetchState.kind === 'loaded' &&
    user?.email.toLowerCase() === fetchState.summary.email.toLowerCase();

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (token === undefined || token.length === 0 || fetchState.kind !== 'loaded') {
      return;
    }
    setFormError(null);

    const body: AcceptOrgInvitationRequest = {};
    if (!activeSessionMatchesInvitee) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setFormError(t('invite.error.password_too_short', { min: MIN_PASSWORD_LENGTH }));
        return;
      }
      if (password.length > MAX_PASSWORD_LENGTH) {
        setFormError(t('invite.error.password_too_long', { max: MAX_PASSWORD_LENGTH }));
        return;
      }
      body.password = password;
      if (displayName.trim().length > 0) {
        body.displayName = displayName.trim();
      }
    }

    setSubmitting(true);
    try {
      await acceptInvitation(token, body, fetchImpl);
      await refresh();
      setAccepted(true);
      toast.success(t('invite.toast.accepted'));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setFormError(coerceErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (accepted) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthPageFrame
      title={t('invite.subtitle')}
      description={t('invite.description')}
      width="lg"
      testId="invite-page-frame"
    >
      {fetchState.kind === 'loading' ? (
        <div className="flex flex-col gap-2.5" role="status" data-testid="invite-loading">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-9 w-full" />
          <span className="sr-only">{t('invite.loading')}</span>
        </div>
      ) : null}

      {fetchState.kind === 'error' ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-bad-line bg-bad-dim p-5"
          data-testid="invite-error"
          role="alert"
        >
          <h2 className="m-0 text-section text-bad-bright">{t('invite.error.title')}</h2>
          <p className="m-0 text-body text-bad-bright">{fetchState.message}</p>
          <Link
            to="/login"
            data-testid="invite-back-to-login"
            className={cx(AUTH_LINK_CLASS, 'w-fit text-paper-mid')}
          >
            {t('common.back_to_login')}
          </Link>
        </div>
      ) : null}

      {fetchState.kind === 'loaded' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          noValidate
          data-testid="invite-form"
        >
          <dl
            className="m-0 flex flex-col gap-3 rounded-lg border border-line-soft bg-raised p-4"
            data-testid="invite-summary"
          >
            <SummaryRow label={t('invite.invite_to')} testId="invite-org-name">
              <span className="text-section text-paper">{fetchState.summary.orgName}</span>
            </SummaryRow>
            <SummaryRow label={t('invite.role')} testId="invite-role">
              <span className="text-body font-medium text-signal">
                {invitationRoleLabel(t, fetchState.summary.role)}
              </span>
            </SummaryRow>
            <SummaryRow label={t('invite.invitee_email')} testId="invite-email">
              <span className="font-mono text-body text-paper-mid">{fetchState.summary.email}</span>
            </SummaryRow>
            {fetchState.summary.invitedByEmail !== null ? (
              <SummaryRow label={t('invite.invited_by')} testId="invite-invited-by">
                <span className="font-mono text-body text-paper-mid">
                  {fetchState.summary.invitedByEmail}
                </span>
              </SummaryRow>
            ) : null}
            <SummaryRow label={t('invite.expires')} testId="invite-expires-at">
              <span className="font-mono text-body text-paper-mid">
                {formatTimestamp(fetchState.summary.expiresAt)}
              </span>
            </SummaryRow>
          </dl>

          {activeSessionMatchesInvitee ? (
            <p className="m-0 text-body text-paper-mid" data-testid="invite-session-match">
              {t('invite.session_match', {
                email: user?.email ?? '',
                org: fetchState.summary.orgName,
              })}
            </p>
          ) : (
            <>
              {status === 'authenticated' && user !== null ? (
                <p
                  className="m-0 rounded-lg border border-warn-line bg-warn-dim/50 px-4 py-3 text-dense text-paper-mid"
                  data-testid="invite-session-mismatch"
                >
                  {t('invite.session_mismatch.body', {
                    current: user.email,
                    target: fetchState.summary.email,
                  })}
                  <Link to="/login" className="text-signal underline underline-offset-2">
                    {t('invite.session_mismatch.switch_link')}
                  </Link>
                  {t('invite.session_mismatch.suffix')}
                </p>
              ) : null}

              <FormField label={t('invite.password_with_hint')} fieldId="invite-password">
                <Input
                  id="invite-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  disabled={submitting}
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={MAX_PASSWORD_LENGTH}
                  data-testid="invite-password"
                />
              </FormField>
              <FormField label={t('invite.display_name')} fieldId="invite-display-name">
                <Input
                  id="invite-display-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  disabled={submitting}
                  maxLength={80}
                  placeholder={t('invite.display_name_placeholder')}
                  data-testid="invite-display-name"
                />
              </FormField>
            </>
          )}

          {formError !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="invite-form-error">
              {formError}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="invite-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting
              ? t('invite.accepting')
              : activeSessionMatchesInvitee
                ? t('invite.join')
                : t('invite.accept')}
          </Button>

          <div>
            <Link to="/login" data-testid="invite-cancel" className={AUTH_LINK_CLASS}>
              {t('invite.cancel')}
            </Link>
          </div>
        </form>
      ) : null}
    </AuthPageFrame>
  );
}

interface SummaryRowProps {
  readonly label: string;
  readonly testId: string;
  readonly children: JSX.Element;
}

function SummaryRow({ label, testId, children }: SummaryRowProps): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}
